import React, { useState, useEffect, useMemo } from 'react'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import Swal from 'sweetalert2'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Modal, Button } from 'react-bootstrap'
import Sorting from '../../../components/company_panel/common/sorting'
import Loader from '../../../components/company_panel/common/loader'
import CustomPagination from '../../../components/company_panel/common/custompagination'
import CustomRangepicker from '../../../components/company_panel/common/rangepicker'
import Breadcrums from '../../../components/company_panel/common/breadcrumbs'
import notFound from '../../../assets/company/img/no-record-found.png'
import { capitalizeFirstLetter, checkPermission, formateDate, formateDateWithTime, getBrandId, getOutletId } from '../../../helpers/commonFunctions'
import { GetInventoryEndOfDayHistories, GetInventoryEndOfDayHistory } from '../../../services/company_panel/inventoryService'

// A picked range covers whole days: from 00:00:00.000 on the first date to
// 23:59:59.999 on the last, sent as ISO.
const startOfDayISO = (value) => {
    const date = new Date(value)
    date.setHours(0, 0, 0, 0)
    return date.toISOString()
}

const endOfDayISO = (value) => {
    const date = new Date(value)
    date.setHours(23, 59, 59, 999)
    return date.toISOString()
}

// Landing screen for "Inventory End Of Day": the counts already submitted, with
// the button that starts a new one. Taking a count lives on the daily/weekly/
// bi-weekly/monthly screens this links out to.
const EndOfDayHistoryList = () => {

    const breadcrumbs = [
        {
            title: "Inventory",
            url: "/company/raw_materials/inventory/list/1"
        },
        {
            title: "End Of Day Histories",
            url: ""
        },
    ]

    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation()

    const [list, setList] = useState([])
    const [dataLength, setDataLength] = useState(0)
    const [itemPerPage] = useState(10)
    const [sorting, setSorting] = useState({})
    const [defaultSorting, setDefaultSorting] = useState(true)
    const [page, setPage] = useState(Number(params?.id) || 1)
    const [loader, setLoader] = useState(true)
    const [search, setSearch] = useState({})
    const [resetdate, setResetDate] = useState(false)
    const [isBrandChanged, setIsBrandChanged] = useState(false)
    const [isOutletChanged, setIsOutletChanged] = useState(false)

    const [exportingId, setExportingId] = useState(null)

    const [showViewModal, setShowViewModal] = useState(false)
    const [viewLoader, setViewLoader] = useState(false)
    const [historyDetail, setHistoryDetail] = useState(null)

    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(!isBrandChanged);
        });
    });

    // Unlike the count screens, this list is per outlet, so a switch reloads it.
    useEffect(() => {
        const onOutletChanged = () => setIsOutletChanged(prev => !prev);
        document.addEventListener('outlet-changed', onOutletChanged);
        return () => document.removeEventListener('outlet-changed', onOutletChanged);
    }, []);

    useEffect(() => {
        setLoader(true)
        const payload = {
            brand_id: getBrandId(),
            outlet_id: getOutletId(),
            page,
            per_page: itemPerPage,
        }
        // The table's sorting uses 1 / -1; this endpoint reads 1 = ASC, 2 = DESC.
        if (sorting?.column) {
            payload.sort = JSON.stringify({ column: sorting.column, order: sorting.order === -1 ? 2 : 1 })
        }
        // business_date, inventory_date and created are independent — only the
        // ranges actually filtered on are sent, each widened to cover the whole
        // day and serialised as ISO so the backend compares like for like.
        const searchPayload = Object.fromEntries(
            Object.entries(search).map(([key, range]) => ([
                key,
                range?.start && range?.end
                    ? { start: startOfDayISO(range.start), end: endOfDayISO(range.end) }
                    : range,
            ]))
        )
        if (Object.keys(searchPayload).length) {
            payload.search = JSON.stringify(searchPayload)
        }
        GetInventoryEndOfDayHistories(payload).then(data => {
            const response = data?.data?.data || data?.data?.response
            setDataLength(response?.count || 0)
            setList(response?.rows || [])
            setLoader(false)
        }).catch(error => {
            console.log("error ====> ", error);
            setDataLength(0)
            setList([])
            setLoader(false)
        })
    }, [location, page, itemPerPage, sorting, search, isBrandChanged, isOutletChanged])

    const handleSort = (e, column) => {
        setDefaultSorting(false);
        let sort = { order: 0, column: column };
        if (e.target.classList.contains("assc")) {
            sort.order = -1;
        } else {
            sort.order = 1;
        }
        setSorting(sort);
    }

    const prepareSearch = (key, value) => {
        let sr = { ...search };
        if (value && Object.keys(value).length) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
    }

    const resetFilter = (e) => {
        e.preventDefault()
        setSearch({})
        setResetDate(!resetdate)
    }

    const viewFunction = (id) => {
        setShowViewModal(true)
        setViewLoader(true)
        setHistoryDetail(null)
        GetInventoryEndOfDayHistory({ id }).then(data => {
            setHistoryDetail(data?.data?.data || data?.data?.response || null)
            setViewLoader(false)
        }).catch(error => {
            console.log("error ====> ", error);
            setHistoryDetail(null)
            setViewLoader(false)
        })
    }

    // The counts live behind the view endpoint, so the row is fetched first and
    // then written out as a sheet — same XLSX + file-saver path as the inventory
    // export. Each line carries its dates so the file stands on its own.
    const exportFunction = (row) => {
        setExportingId(row.id)
        GetInventoryEndOfDayHistory({ id: row.id }).then(data => {
            const detail = data?.data?.data || data?.data?.response || null
            const rows = detail?.end_of_day_inventory_counts || []
            if (!rows.length) {
                Swal.fire({
                    customClass: 'swal-wide',
                    icon: 'error',
                    title: 'No counts to export for this end of day',
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                    position: 'top-right',
                })
                return
            }
            // The dates head the sheet once, then a blank line, then the count
            // table — rather than repeating the dates on every row.
            // The view response carries no created_at, so it comes off the listed row.
            const createdAt = detail?.created_at || row.created_at
            const sheet = [
                ['Business Date', 'Inventory Date', 'System Date', 'Created At'],
                [
                    detail?.business_date ? formateDate(detail.business_date) : '-',
                    detail?.inventory_date ? formateDate(detail.inventory_date) : '-',
                    detail?.system_date ? formateDateWithTime(detail.system_date) : '-',
                    createdAt ? formateDateWithTime(createdAt) : '-',
                ],
                [],
                ['Name', 'Category', 'Sub Category', 'Unit Per Measure', 'Physical Count'],
                ...rows.map(count => ([
                    count.inventory_name ? capitalizeFirstLetter(count.inventory_name) : '-',
                    count.category ? capitalizeFirstLetter(count.category) : '-',
                    count.sub_category ? capitalizeFirstLetter(count.sub_category) : '-',
                    count.uom ? capitalizeFirstLetter(count.uom) : '-',
                    // A real number so the column can be summed in the sheet.
                    isNaN(Number(count.physical_count)) ? count.physical_count : Number(count.physical_count),
                ])),
            ]
            const ws = XLSX.utils.aoa_to_sheet(sheet)
            // Fixed widths so nothing opens truncated behind the next column.
            ws['!cols'] = [{ wch: 30 }, { wch: 24 }, { wch: 24 }, { wch: 24 }, { wch: 18 }]
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
            const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
            saveAs(blob, `inventory-end-of-day-${row.id}.xlsx`)
        }).catch(error => {
            console.log("error ====> ", error);
            Swal.fire({
                customClass: 'swal-wide',
                icon: 'error',
                title: 'Could not export this end of day count',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                position: 'top-right',
            })
        }).finally(() => {
            setExportingId(null)
        })
    }

    const closeViewModal = () => {
        setShowViewModal(false)
        setHistoryDetail(null)
    }

    const counts = historyDetail?.end_of_day_inventory_counts || []

    return (
        <>
            <div className="animation_fade">
                <div className='page-header d-flex align-items-center'>
                    <div className='page-header-left'>
                        <h4>Inventory End Of Day Histories</h4>
                        <Breadcrums data={breadcrumbs} />
                    </div>
                    {/* the count pages are where an end of inventory is added */}
                    {checkPermission('store_end_of_inventories', 'add') ?
                        <div className='page-header-right ms-auto'>
                            <button
                                className="btn btn-primary ms-2"
                                type="button"
                                onClick={() => navigate(`/company/raw_materials/inventory/end_of_day/daily/list/1`)}
                            >
                                End Of Day
                            </button>
                        </div>
                        : null}
                </div>

                <div className="card custom-card overflow-hidden">
                    <div className='gap-20'>
                        <div className='filter-header d-flex justify-content-end align-items-center flex-wrap gap-3'>
                            <div className="d-flex align-items-center flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <label className='mb-0 text-nowrap'>Business Date</label>
                                <CustomRangepicker
                                    business_date={true}
                                    GetDateRange={(e) => { prepareSearch("business_date", e) }}
                                    resetdate={resetdate}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label className='mb-0 text-nowrap'>Inventory Date</label>
                                <CustomRangepicker
                                    GetDateRange={(e) => { prepareSearch("inventory_date", e) }}
                                    resetdate={resetdate}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label className='mb-0 text-nowrap'>Created At</label>
                                <CustomRangepicker
                                    GetDateRange={(e) => { prepareSearch("created", e) }}
                                    resetdate={resetdate}
                                />
                            </div>
                            <button type="reset" value='Reset' onClick={resetFilter} className="btn btn-primary mr-2">Reset Filter</button>
                            </div>
                        </div>
                    </div>

                    <div className="tabler-wrap">
                        <div className="table-responsive">
                            <table className="table table-custom">
                                <thead>
                                    <tr>
                                        <th width="10%">
                                            <div className='d-inline-flex align-items-start'>
                                                <span className='me-2'>Id</span>
                                                <Sorting sort={sorting} handleSort={handleSort} defaultSorting={defaultSorting} column="id" />
                                            </div>
                                        </th>
                                        <th width="25%">
                                            <div className='sorting_column'>
                                                <span>Business Date</span>
                                                <Sorting sort={sorting} handleSort={handleSort} column="business_date" />
                                            </div>
                                        </th>
                                        <th width="25%">
                                            <div className='sorting_column'>
                                                <span>Inventory Date</span>
                                                <Sorting sort={sorting} handleSort={handleSort} column="inventory_date" />
                                            </div>
                                        </th>
                                        <th width="25%">
                                            <div className='sorting_column'>
                                                <span>Created At</span>
                                                <Sorting sort={sorting} handleSort={handleSort} column="created_at" />
                                            </div>
                                        </th>
                                        <th className='action_head text-end' width="10%">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ?
                                        <tr>
                                            <td colSpan={5}><Loader /></td>
                                        </tr> : <>
                                            {list.length ? list.map((row, i) => (
                                                <tr key={row.id || i}>
                                                    <td>{row.id}</td>
                                                    <td>{row.business_date ? formateDate(row.business_date) : '-'}</td>
                                                    <td>{row.inventory_date ? formateDate(row.inventory_date) : '-'}</td>
                                                    <td>{row.created_at ? formateDateWithTime(row.created_at) : '-'}</td>
                                                    <td className='text-end'>
                                                        <div className="dropdown d-inline-block">
                                                            <button className="btn btn-icon btn-secondary" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                                <i className='ti ti-dots-vertical'></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                <li>
                                                                    <div className='dropdown-item' onClick={() => { viewFunction(row.id) }}>
                                                                        <i className="ti ti-eye" aria-hidden="true"></i> View
                                                                    </div>
                                                                </li>
                                                                <li>
                                                                    <div className='dropdown-item' onClick={() => { exportFunction(row) }}>
                                                                        <i className="ti ti-download" aria-hidden="true"></i> {exportingId === row.id ? 'Exporting...' : 'Export In Excel'}
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : <tr>
                                                <td colSpan={5} className="text-center">
                                                    <img src={notFound} style={{ width: "5%" }} alt="Not Found" />
                                                    <p>No record found</p>
                                                </td>
                                            </tr>}
                                        </>}
                                </tbody>
                            </table>
                        </div>
                        <div className='tabler-footer'>
                            <div className='d-flex align-items-center'>
                                <div id="example1_info" role="status" className="table-result" aria-live="polite"><b>Total Records : {dataLength ? dataLength : "0"}</b></div>
                                {dataLength && dataLength > 0 ?
                                    <CustomPagination
                                        datalength={dataLength}
                                        itemperpage={itemPerPage}
                                        setPage={setPage}
                                        currentPage={page}
                                        pageRoute={[{ name: "end-of-day-history", path: "/company/raw_materials/inventory/end_of_day/history/list" }]}
                                    /> : ""}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showViewModal} onHide={closeViewModal} size="lg" scrollable centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        End Of Day Count
                        {historyDetail?.id ? <small className="text-muted ms-2">#{historyDetail.id}</small> : null}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh' }}>
                    {viewLoader ? <Loader /> : historyDetail ? (
                        <>
                            <div className="date-display mb-4 d-flex flex-wrap">
                                <div className="d-flex flex-column align-items-start me-5">
                                    <span>Date Of Business</span>
                                    <span className="date-value fw-bold">{historyDetail.business_date ? formateDate(historyDetail.business_date) : '-'}</span>
                                </div>
                                <div className="d-flex flex-column align-items-start me-5">
                                    <span>Date Of Inventory</span>
                                    <span className="date-value fw-bold">{historyDetail.inventory_date ? formateDate(historyDetail.inventory_date) : '-'}</span>
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <span>Date Of System</span>
                                    <span className="date-value fw-bold">{historyDetail.system_date ? formateDateWithTime(historyDetail.system_date) : '-'}</span>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-custom">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Category</th>
                                            <th>Sub Category</th>
                                            <th>Unit Per Measure</th>
                                            <th className='text-end'>Physical Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {counts.length ? counts.map((count, i) => (
                                            <tr key={count.inventory_id || i}>
                                                <td>{count.inventory_name ? capitalizeFirstLetter(count.inventory_name) : '-'}</td>
                                                <td>{count.category ? capitalizeFirstLetter(count.category) : '-'}</td>
                                                <td>{count.sub_category ? capitalizeFirstLetter(count.sub_category) : '-'}</td>
                                                <td>{count.uom ? capitalizeFirstLetter(count.uom) : '-'}</td>
                                                <td className='text-end'>{count.physical_count != null ? count.physical_count : '-'}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={5} className="text-center">No counts recorded</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div role="status" aria-live="polite"><b>Total Records : {counts.length}</b></div>
                        </>
                    ) : <p className='text-center text-muted mb-0'>Could not load this end of day count</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeViewModal}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default EndOfDayHistoryList
